import React, { createContext, useCallback, useContext, useState } from "react";
import ErrorBoundary from "components/ErrorBoundary";
import type { SfoContentOverride, SfoData } from "./useSfoContent";

const SfoModal = React.lazy(() => import("./SfoModal"));

export type SfoModalContent = SfoContentOverride & {
  sfo: SfoData;
  email?: string;
  formName?: string;
  onRequestClose?: () => void;
  onFormSubmitted?: () => void;
};

const SfoModalContext = createContext<
  ((content: SfoModalContent) => void) | undefined
>(undefined);

export function useSfoModal() {
  const openModal = useContext(SfoModalContext);
  if (!openModal) throw new Error("useSfoModal requires SfoModalProvider");
  return openModal;
}

export function SfoModalProvider({ children }: React.PropsWithChildren) {
  const [content, setContent] = useState<SfoModalContent>();
  const closeModal = useCallback(() => {
    setContent(undefined);
    content?.onRequestClose?.();
  }, [content]);

  return (
    <SfoModalContext.Provider value={setContent}>
      {children}
      {content && (
        <ErrorBoundary>
          <React.Suspense fallback={null}>
            <SfoModal {...content} onRequestClose={closeModal} />
          </React.Suspense>
        </ErrorBoundary>
      )}
    </SfoModalContext.Provider>
  );
}
